import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { strategicObjectivesApi, goalsApi, chatApi, visionStoryApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Eye, 
  Target, 
  Flag, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  ChevronDown, 
  ChevronUp,
  Calendar,
  TrendingUp,
  CheckCircle,
  Circle,
  Loader2,
  Lock,
  Sparkles,
  Map,
  List,
  ArrowDown,
  GripVertical,
  Heart,
  Activity,
  Play,
  Video,
  Volume2,
  Image,
  RefreshCw,
  Download
} from 'lucide-react';

// Sortable Priority Item Component
const SortablePriorityItem = ({ priority, index, expandedPriorities, togglePriorityExpand, startEditPriority, handleDeletePriority, editingPriorityId, priorityForm, setPriorityForm, handleUpdatePriority, formatDate, getProgressColor }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: priority.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className="border border-slate-200 dark:border-[#2D2D44] rounded-xl overflow-hidden"
      data-testid={`priority-item-${index}`}
    >
      {editingPriorityId === priority.id ? (
        <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50">
          <div className="space-y-3">
            <Input
              value={priorityForm.title}
              onChange={(e) => setPriorityForm({ ...priorityForm, title: e.target.value })}
              className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
            />
            <Textarea
              value={priorityForm.description}
              onChange={(e) => setPriorityForm({ ...priorityForm, description: e.target.value })}
              className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
            />
            <Input
              type="date"
              value={priorityForm.target_date}
              onChange={(e) => setPriorityForm({ ...priorityForm, target_date: e.target.value })}
              className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
            />
            <div className="flex gap-2">
              <Button onClick={() => handleUpdatePriority(priority.id)} className="bg-purple-500 hover:bg-purple-600">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="ghost" onClick={() => setPriorityForm({ title: '', description: '', target_date: '' })}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="p-4 flex items-center gap-2">
            {/* Drag Handle */}
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
              data-testid={`priority-drag-handle-${index}`}
            >
              <GripVertical className="w-5 h-5 text-slate-400" />
            </button>
            
            <div 
              className="flex items-center justify-between flex-1 cursor-pointer"
              onClick={() => togglePriorityExpand(priority.id)}
            >
              <div className="flex items-center gap-3 flex-1">
                <div className={`p-2 rounded-lg ${priority.progress_percentage >= 100 ? 'bg-emerald-500/20' : 'bg-purple-500/20'}`}>
                  {priority.progress_percentage >= 100 ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Circle className="w-5 h-5 text-purple-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-800 dark:text-slate-100">{priority.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1">
                    <span>{priority.goals_count} goals</span>
                    <span>{priority.goals_completed} completed</span>
                    {priority.target_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(priority.target_date)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className={`text-xl font-bold font-mono ${getProgressColor(priority.progress_percentage)}`}>
                    {priority.progress_percentage}%
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); startEditPriority(priority); }}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); handleDeletePriority(priority.id); }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  {expandedPriorities[priority.id] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="px-4 pb-4">
            <Progress value={priority.progress_percentage} className="h-2 bg-slate-200 dark:bg-slate-700" />
          </div>

          {/* Expanded Details */}
          {expandedPriorities[priority.id] && (
            <div className="px-4 pb-4 pt-2 border-t border-slate-200 dark:border-[#2D2D44] bg-slate-50 dark:bg-[#1A1A2E]/30">
              {priority.description && (
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">{priority.description}</p>
              )}
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  <span className="font-medium text-emerald-400">{priority.goals_completed}</span> of{' '}
                  <span className="font-medium">{priority.goals_count}</span> goals completed
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = `/goals?priority=${priority.id}`}
                  className="text-xs"
                >
                  View Goals
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StrategicObjectives = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState(null);
  const [goals, setGoals] = useState([]);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('detail'); // 'detail' or 'map'
  
  // Edit states
  const [editingVision, setEditingVision] = useState(false);
  const [editingMission, setEditingMission] = useState(false);
  const [visionForm, setVisionForm] = useState({ title: '', description: '' });
  const [missionForm, setMissionForm] = useState({ title: '', description: '' });
  
  // Priority states
  const [showAddPriority, setShowAddPriority] = useState(false);
  const [editingPriorityId, setEditingPriorityId] = useState(null);
  const [priorityForm, setPriorityForm] = useState({ title: '', description: '', target_date: '' });
  const [expandedPriorities, setExpandedPriorities] = useState({});
  
  // AI Assist states
  const [aiLoading, setAiLoading] = useState({ vision: false, mission: false, priority: false });
  const [aiContext, setAiContext] = useState('');
  const [showAiContextInput, setShowAiContextInput] = useState(null);
  
  // Vision Story state
  const [showVisionStory, setShowVisionStory] = useState(false);
  const [visionStory, setVisionStory] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyFormat, setStoryFormat] = useState('story');
  const [storyVoice, setStoryVoice] = useState('nova');
  const [pollingStory, setPollingStory] = useState(null);
  
  const effectivePlan = user?.promo_plan || user?.plan || 'free_trial';
  const hasAccess = effectivePlan === 'enterprise' || user?.is_admin;

  useEffect(() => {
    if (hasAccess) {
      fetchObjectives();
      fetchGoals();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchGoals = async () => {
    try {
      const response = await goalsApi.getAll();
      setGoals(response.data || []);
    } catch (err) {
      console.error('Error fetching goals:', err);
    }
  };

  const fetchObjectives = async () => {
    try {
      setLoading(true);
      const response = await strategicObjectivesApi.getObjectives();
      setObjectives(response.data);
      setVisionForm({ title: response.data.vision?.title || '', description: response.data.vision?.description || '' });
      setMissionForm({ title: response.data.mission?.title || '', description: response.data.mission?.description || '' });
    } catch (err) {
      console.error('Error fetching objectives:', err);
      setError('Failed to load strategic objectives');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVision = async () => {
    try {
      await strategicObjectivesApi.updateVision(visionForm);
      toast.success('Vision updated successfully');
      setEditingVision(false);
      fetchObjectives();
    } catch (err) {
      toast.error('Failed to update vision');
    }
  };

  const handleSaveMission = async () => {
    try {
      await strategicObjectivesApi.updateMission(missionForm);
      toast.success('Mission updated successfully');
      setEditingMission(false);
      fetchObjectives();
    } catch (err) {
      toast.error('Failed to update mission');
    }
  };

  const handleAddPriority = async () => {
    if (!priorityForm.title.trim()) {
      toast.error('Priority title is required');
      return;
    }
    try {
      await strategicObjectivesApi.createPriority(priorityForm);
      toast.success('Priority created successfully');
      setShowAddPriority(false);
      setPriorityForm({ title: '', description: '', target_date: '' });
      fetchObjectives();
    } catch (err) {
      toast.error('Failed to create priority');
    }
  };

  const handleUpdatePriority = async (priorityId) => {
    try {
      await strategicObjectivesApi.updatePriority(priorityId, priorityForm);
      toast.success('Priority updated successfully');
      setEditingPriorityId(null);
      setPriorityForm({ title: '', description: '', target_date: '' });
      fetchObjectives();
    } catch (err) {
      toast.error('Failed to update priority');
    }
  };

  const handleDeletePriority = async (priorityId) => {
    if (!confirm('Are you sure? Goals linked to this priority will be unlinked.')) return;
    try {
      await strategicObjectivesApi.deletePriority(priorityId);
      toast.success('Priority deleted successfully');
      fetchObjectives();
    } catch (err) {
      toast.error('Failed to delete priority');
    }
  };

  const startEditPriority = (priority) => {
    setEditingPriorityId(priority.id);
    setPriorityForm({
      title: priority.title,
      description: priority.description || '',
      target_date: priority.target_date || ''
    });
  };

  const togglePriorityExpand = (priorityId) => {
    setExpandedPriorities(prev => ({
      ...prev,
      [priorityId]: !prev[priorityId]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 75) return 'text-emerald-400';
    if (percentage >= 50) return 'text-amber-400';
    if (percentage >= 25) return 'text-orange-400';
    return 'text-slate-400';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-[#26C6DA]';
    if (score >= 40) return 'text-amber-400';
    if (score >= 20) return 'text-orange-400';
    return 'text-red-400';
  };

  const getHealthScoreBg = (score) => {
    if (score >= 80) return 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30';
    if (score >= 60) return 'from-[#26C6DA]/20 to-[#26C6DA]/10 border-[#26C6DA]/30';
    if (score >= 40) return 'from-amber-500/20 to-amber-600/10 border-amber-500/30';
    if (score >= 20) return 'from-orange-500/20 to-orange-600/10 border-orange-500/30';
    return 'from-red-500/20 to-red-600/10 border-red-500/30';
  };

  // AI Assist handler
  const handleAiSuggest = async (type) => {
    setAiLoading(prev => ({ ...prev, [type]: true }));
    try {
      const currentValue = type === 'vision' 
        ? `${visionForm.title} ${visionForm.description}`.trim()
        : type === 'mission'
        ? `${missionForm.title} ${missionForm.description}`.trim()
        : `${priorityForm.title} ${priorityForm.description}`.trim();
      
      const response = await chatApi.getStrategicSuggestion(type, aiContext, currentValue || null);
      const suggestion = response.data.suggestion;
      
      if (type === 'vision' || type === 'mission') {
        // Parse the response for title and description
        const titleMatch = suggestion.match(/TITLE:\s*(.+?)(?:\n|DESCRIPTION:)/s);
        const descMatch = suggestion.match(/DESCRIPTION:\s*(.+?)$/s);
        
        const title = titleMatch ? titleMatch[1].trim() : '';
        const description = descMatch ? descMatch[1].trim() : '';
        
        if (type === 'vision') {
          setVisionForm({ title, description });
        } else {
          setMissionForm({ title, description });
        }
        toast.success(`AI suggestion applied to ${type}!`);
      } else if (type === 'priority') {
        // For priorities, show the suggestions in a toast or apply the first one
        const priorityMatch = suggestion.match(/Title:\s*(.+?)(?:\n|Description:)/);
        const prioDescMatch = suggestion.match(/Description:\s*(.+?)(?:\n\n|PRIORITY|$)/s);
        
        if (priorityMatch) {
          setPriorityForm(prev => ({
            ...prev,
            title: priorityMatch[1].trim(),
            description: prioDescMatch ? prioDescMatch[1].trim() : prev.description
          }));
          toast.success('AI suggestion applied! Check the other suggestions in the AI response.');
        }
        // Also log the full response for user reference
        console.log('AI Priority Suggestions:', suggestion);
      }
      
      setShowAiContextInput(null);
      setAiContext('');
    } catch (err) {
      console.error('AI suggestion error:', err);
      toast.error('Failed to get AI suggestion. Please try again.');
    } finally {
      setAiLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Vision Story handlers
  const handleGenerateVisionStory = async () => {
    if (!objectives?.vision?.title) {
      toast.error('Please set your Vision first');
      return;
    }
    
    setStoryLoading(true);
    try {
      const response = await visionStoryApi.generate(storyFormat, storyVoice);
      setVisionStory(response.data);
      
      // If audio, video, or slideshow, poll for completion
      if (storyFormat === 'audio' || storyFormat === 'video' || storyFormat === 'slideshow') {
        setPollingStory(response.data.story_id);
      }
      
      toast.success('Vision story generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate vision story');
    } finally {
      setStoryLoading(false);
    }
  };

  // Poll for audio/video/slideshow completion
  useEffect(() => {
    if (!pollingStory) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await visionStoryApi.get(pollingStory);
        setVisionStory(response.data);
        
        // Check if media is ready
        if (response.data.audio_status === 'complete' || 
            response.data.video_status === 'complete' ||
            response.data.slideshow_status === 'complete') {
          setPollingStory(null);
          toast.success('Your media is ready!');
        } else if (response.data.audio_status === 'failed' || 
                   response.data.video_status === 'failed' ||
                   response.data.slideshow_status === 'failed') {
          setPollingStory(null);
          toast.error('Media generation failed. You can still read the story.');
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [pollingStory]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = objectives.priorities.findIndex(p => p.id === active.id);
      const newIndex = objectives.priorities.findIndex(p => p.id === over.id);
      
      const newPriorities = arrayMove(objectives.priorities, oldIndex, newIndex);
      
      // Optimistic update
      setObjectives(prev => ({ ...prev, priorities: newPriorities }));
      
      // Send to backend
      try {
        await strategicObjectivesApi.reorderPriorities(newPriorities.map(p => p.id));
        toast.success('Priorities reordered');
      } catch (err) {
        toast.error('Failed to reorder priorities');
        fetchObjectives(); // Revert on error
      }
    }
  };

  // Non-enterprise view
  if (!hasAccess) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Strategic Objectives
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Define your Vision, Mission, and Strategic Priorities
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500/10 to-[#26C6DA]/10 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-12 text-center">
          <Lock className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
            Enterprise Feature
          </h2>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-6">
            Strategic Objectives helps you align your goals with your organization's Vision, Mission, and Strategic Priorities. 
            Upgrade to Enterprise to unlock this feature.
          </p>
          <Button 
            className="bg-gradient-to-r from-purple-500 to-[#26C6DA] text-white"
            onClick={() => window.location.href = '/billing'}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Upgrade to Enterprise
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#26C6DA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400">{error}</p>
        <Button onClick={fetchObjectives} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="strategic-objectives-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            Strategic Objectives
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Define your Vision, Mission, and Strategic Priorities
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Play My Vision Button */}
          <Button
            onClick={() => setShowVisionStory(true)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            data-testid="play-vision-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Play My Vision
          </Button>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-[#1A1A2E] rounded-lg p-1">
            <button
              onClick={() => setViewMode('detail')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'detail' 
                  ? 'bg-[#26C6DA] text-white' 
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              data-testid="view-detail-btn"
            >
              <List className="w-4 h-4" />
              Detail
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map' 
                  ? 'bg-[#26C6DA] text-white' 
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              data-testid="view-map-btn"
            >
              <Map className="w-4 h-4" />
              Strategy Map
            </button>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-purple-300">
              {objectives?.overall_progress || 0}% Overall Progress
            </span>
          </div>
        </div>
      </div>

      {/* Strategy Map View */}
      {viewMode === 'map' && (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-8" data-testid="strategy-map">
          <div className="flex flex-col items-center">
            {/* Vision - Top Level */}
            <div className="w-full max-w-2xl">
              <div className={`p-6 rounded-2xl border-2 ${
                objectives?.vision?.title 
                  ? 'bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/50' 
                  : 'bg-slate-100 dark:bg-slate-800/50 border-dashed border-slate-300 dark:border-slate-600'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Eye className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-500">Vision</span>
                </div>
                {objectives?.vision?.title ? (
                  <>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{objectives.vision.title}</h3>
                    {objectives.vision.description && (
                      <p className="text-slate-600 dark:text-slate-300 mt-2 text-sm">{objectives.vision.description}</p>
                    )}
                  </>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 italic">No vision defined yet</p>
                )}
              </div>
            </div>

            {/* Arrow Down */}
            <div className="flex flex-col items-center my-4">
              <div className="w-0.5 h-8 bg-gradient-to-b from-amber-500 to-blue-500"></div>
              <ArrowDown className="w-5 h-5 text-blue-400 -mt-1" />
            </div>

            {/* Mission - Second Level */}
            <div className="w-full max-w-2xl">
              <div className={`p-6 rounded-2xl border-2 ${
                objectives?.mission?.title 
                  ? 'bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/50' 
                  : 'bg-slate-100 dark:bg-slate-800/50 border-dashed border-slate-300 dark:border-slate-600'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <Target className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-500">Mission</span>
                </div>
                {objectives?.mission?.title ? (
                  <>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">{objectives.mission.title}</h3>
                    {objectives.mission.description && (
                      <p className="text-slate-600 dark:text-slate-300 mt-2 text-sm">{objectives.mission.description}</p>
                    )}
                  </>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 italic">No mission defined yet</p>
                )}
              </div>
            </div>

            {/* Arrow Down */}
            <div className="flex flex-col items-center my-4">
              <div className="w-0.5 h-8 bg-gradient-to-b from-blue-500 to-purple-500"></div>
              <ArrowDown className="w-5 h-5 text-purple-400 -mt-1" />
            </div>

            {/* Strategic Priorities - Third Level */}
            <div className="w-full">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Flag className="w-4 h-4 text-purple-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-purple-500">Strategic Priorities</span>
              </div>
              {objectives?.priorities?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {objectives.priorities.map((priority, index) => {
                    const priorityGoals = goals.filter(g => g.priority_id === priority.id);
                    return (
                      <div 
                        key={priority.id} 
                        className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/30"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-slate-800 dark:text-slate-100">{priority.title}</h4>
                          <span className={`text-lg font-bold font-mono ${getProgressColor(priority.progress_percentage)}`}>
                            {priority.progress_percentage}%
                          </span>
                        </div>
                        <Progress value={priority.progress_percentage} className="h-1.5 mb-3 bg-slate-700" />
                        
                        {/* Linked Goals */}
                        <div className="space-y-1.5">
                          {priorityGoals.length > 0 ? (
                            priorityGoals.slice(0, 3).map(goal => (
                              <div key={goal.id} className="flex items-center gap-2 text-xs">
                                {goal.status === 'completed' ? (
                                  <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                                ) : (
                                  <Circle className="w-3 h-3 text-slate-500 flex-shrink-0" />
                                )}
                                <span className={`truncate ${goal.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-600 dark:text-slate-300'}`}>
                                  {goal.title}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-slate-500 dark:text-slate-400 italic">No goals linked</p>
                          )}
                          {priorityGoals.length > 3 && (
                            <p className="text-xs text-purple-400">+{priorityGoals.length - 3} more goals</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center p-8 bg-slate-100 dark:bg-slate-800/30 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400 italic">No priorities defined yet</p>
                </div>
              )}
            </div>

            {/* Arrow Down to Goals */}
            {objectives?.priorities?.length > 0 && (
              <>
                <div className="flex flex-col items-center my-4">
                  <div className="w-0.5 h-8 bg-gradient-to-b from-purple-500 to-[#26C6DA]"></div>
                  <ArrowDown className="w-5 h-5 text-[#26C6DA] -mt-1" />
                </div>

                {/* Goals Summary */}
                <div className="w-full max-w-2xl">
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-[#26C6DA]/10 to-[#26C6DA]/5 border border-[#26C6DA]/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#26C6DA]/20 rounded-lg">
                          <Target className="w-5 h-5 text-[#26C6DA]" />
                        </div>
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wider text-[#26C6DA]">Goals</span>
                          <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                            {objectives.total_goals} total goals, {objectives.total_goals_completed} completed
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-[#26C6DA] font-mono">{objectives.overall_progress}%</span>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Overall Progress</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Detail View */}
      {viewMode === 'detail' && (
        <>
      {/* Strategy Health Score */}
      {objectives?.health_score && (
        <div className={`bg-gradient-to-r ${getHealthScoreBg(objectives.health_score.percentage)} backdrop-blur-sm rounded-2xl border p-6`} data-testid="health-score-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-xl ${objectives.health_score.percentage >= 60 ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                <Activity className={`w-6 h-6 ${getHealthScoreColor(objectives.health_score.percentage)}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                  Strategy Health Score
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">How well-defined is your strategy?</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-4xl font-bold font-mono ${getHealthScoreColor(objectives.health_score.percentage)}`}>
                {objectives.health_score.percentage}%
              </p>
            </div>
          </div>
          
          {/* Health Score Breakdown */}
          <div className="grid grid-cols-5 gap-3 mt-4">
            <div className="text-center p-3 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-lg">
              <Eye className={`w-5 h-5 mx-auto mb-1 ${objectives.health_score.breakdown.vision_defined > 0 ? 'text-amber-400' : 'text-slate-500'}`} />
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Vision</p>
              <p className={`text-sm font-bold ${objectives.health_score.breakdown.vision_defined > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {objectives.health_score.breakdown.vision_defined}/20
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-lg">
              <Target className={`w-5 h-5 mx-auto mb-1 ${objectives.health_score.breakdown.mission_defined > 0 ? 'text-blue-400' : 'text-slate-500'}`} />
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Mission</p>
              <p className={`text-sm font-bold ${objectives.health_score.breakdown.mission_defined > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {objectives.health_score.breakdown.mission_defined}/20
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-lg">
              <Flag className={`w-5 h-5 mx-auto mb-1 ${objectives.health_score.breakdown.has_priorities > 0 ? 'text-purple-400' : 'text-slate-500'}`} />
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Priorities</p>
              <p className={`text-sm font-bold ${objectives.health_score.breakdown.has_priorities > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {objectives.health_score.breakdown.has_priorities}/20
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-lg">
              <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${objectives.health_score.breakdown.goals_linked > 0 ? 'text-[#26C6DA]' : 'text-slate-500'}`} />
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Linked</p>
              <p className={`text-sm font-bold ${objectives.health_score.breakdown.goals_linked > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {objectives.health_score.breakdown.goals_linked}/20
              </p>
            </div>
            <div className="text-center p-3 bg-white/50 dark:bg-[#1A1A2E]/50 rounded-lg">
              <TrendingUp className={`w-5 h-5 mx-auto mb-1 ${objectives.health_score.breakdown.goal_progress > 0 ? 'text-emerald-400' : 'text-slate-500'}`} />
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Progress</p>
              <p className={`text-sm font-bold ${objectives.health_score.breakdown.goal_progress > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                {objectives.health_score.breakdown.goal_progress}/20
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overall Progress Summary */}
      <div className="bg-gradient-to-r from-[#26C6DA]/10 to-purple-500/10 backdrop-blur-sm rounded-2xl border border-[#26C6DA]/30 p-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-[#26C6DA] font-mono">{objectives?.total_goals || 0}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Total Goals</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-emerald-400 font-mono">{objectives?.total_goals_completed || 0}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-400 font-mono">{objectives?.priorities?.length || 0}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">Priorities</p>
          </div>
        </div>
        <div className="mt-4">
          <Progress value={objectives?.overall_progress || 0} className="h-3 bg-slate-700" />
        </div>
      </div>

      {/* Vision Section */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="vision-section">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Eye className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Vision
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">What you aspire to become</p>
            </div>
          </div>
          {!editingVision && (
            <Button variant="ghost" size="sm" onClick={() => setEditingVision(true)} data-testid="edit-vision-btn">
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {editingVision ? (
          <div className="space-y-4">
            <Input
              placeholder="Vision Title"
              value={visionForm.title}
              onChange={(e) => setVisionForm({ ...visionForm, title: e.target.value })}
              className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-300 dark:border-[#2D2D44]"
              data-testid="vision-title-input"
            />
            <Textarea
              placeholder="Describe your vision..."
              value={visionForm.description}
              onChange={(e) => setVisionForm({ ...visionForm, description: e.target.value })}
              className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-300 dark:border-[#2D2D44] min-h-[100px]"
              data-testid="vision-description-input"
            />
            
            {/* AI Context Input */}
            {showAiContextInput === 'vision' && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-2">
                <p className="text-sm text-purple-300">Tell the AI about your industry, goals, or values for better suggestions:</p>
                <Input
                  placeholder="e.g., 'I run a health coaching business focused on busy professionals'"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-purple-500/30"
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleAiSuggest('vision')} 
                    disabled={aiLoading.vision}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {aiLoading.vision ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowAiContextInput(null); setAiContext(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button onClick={handleSaveVision} className="bg-[#26C6DA] hover:bg-[#26C6DA]/80" data-testid="save-vision-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditingVision(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              {showAiContextInput !== 'vision' && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowAiContextInput('vision')}
                  className="ml-auto border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                  data-testid="ai-assist-vision-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Assist
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="pl-12">
            {objectives?.vision?.title ? (
              <>
                <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">{objectives.vision.title}</h3>
                {objectives.vision.description && (
                  <p className="text-slate-600 dark:text-slate-300 mt-2">{objectives.vision.description}</p>
                )}
              </>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 italic">Click edit to define your vision...</p>
            )}
          </div>
        )}
      </div>

      {/* Mission Section */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="mission-section">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/20 rounded-xl">
              <Target className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Mission
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Your purpose and what you do</p>
            </div>
          </div>
          {!editingMission && (
            <Button variant="ghost" size="sm" onClick={() => setEditingMission(true)} data-testid="edit-mission-btn">
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {editingMission ? (
          <div className="space-y-4">
            <Input
              placeholder="Mission Title"
              value={missionForm.title}
              onChange={(e) => setMissionForm({ ...missionForm, title: e.target.value })}
              className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-300 dark:border-[#2D2D44]"
              data-testid="mission-title-input"
            />
            <Textarea
              placeholder="Describe your mission..."
              value={missionForm.description}
              onChange={(e) => setMissionForm({ ...missionForm, description: e.target.value })}
              className="bg-slate-100 dark:bg-[#1A1A2E] border-slate-300 dark:border-[#2D2D44] min-h-[100px]"
              data-testid="mission-description-input"
            />
            
            {/* AI Context Input */}
            {showAiContextInput === 'mission' && (
              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-2">
                <p className="text-sm text-purple-300">Tell the AI about your industry, audience, or unique approach:</p>
                <Input
                  placeholder="e.g., 'We serve small business owners with affordable marketing solutions'"
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  className="bg-slate-100 dark:bg-[#1A1A2E] border-purple-500/30"
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleAiSuggest('mission')} 
                    disabled={aiLoading.mission}
                    className="bg-purple-500 hover:bg-purple-600"
                  >
                    {aiLoading.mission ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate
                  </Button>
                  <Button variant="ghost" onClick={() => { setShowAiContextInput(null); setAiContext(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button onClick={handleSaveMission} className="bg-[#26C6DA] hover:bg-[#26C6DA]/80" data-testid="save-mission-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="ghost" onClick={() => setEditingMission(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              {showAiContextInput !== 'mission' && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowAiContextInput('mission')}
                  className="ml-auto border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                  data-testid="ai-assist-mission-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Assist
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="pl-12">
            {objectives?.mission?.title ? (
              <>
                <h3 className="text-lg font-medium text-slate-800 dark:text-slate-100">{objectives.mission.title}</h3>
                {objectives.mission.description && (
                  <p className="text-slate-600 dark:text-slate-300 mt-2">{objectives.mission.description}</p>
                )}
              </>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 italic">Click edit to define your mission...</p>
            )}
          </div>
        )}
      </div>

      {/* Strategic Priorities Section */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="priorities-section">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <Flag className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Strategic Priorities
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Key focus areas that drive your goals</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowAddPriority(true)} 
            className="bg-purple-500 hover:bg-purple-600"
            data-testid="add-priority-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Priority
          </Button>
        </div>

        {/* Add Priority Form */}
        {showAddPriority && (
          <div className="mb-6 p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl border border-purple-500/30">
            <h3 className="font-medium text-slate-800 dark:text-slate-100 mb-3">New Strategic Priority</h3>
            <div className="space-y-3">
              <Input
                placeholder="Priority Title"
                value={priorityForm.title}
                onChange={(e) => setPriorityForm({ ...priorityForm, title: e.target.value })}
                className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
                data-testid="priority-title-input"
              />
              <Textarea
                placeholder="Description (optional)"
                value={priorityForm.description}
                onChange={(e) => setPriorityForm({ ...priorityForm, description: e.target.value })}
                className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
                data-testid="priority-description-input"
              />
              <Input
                type="date"
                value={priorityForm.target_date}
                onChange={(e) => setPriorityForm({ ...priorityForm, target_date: e.target.value })}
                className="bg-white dark:bg-[#111827] border-slate-300 dark:border-[#2D2D44]"
                data-testid="priority-date-input"
              />
              
              {/* AI Context Input */}
              {showAiContextInput === 'priority' && (
                <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-2">
                  <p className="text-sm text-purple-300">Tell the AI about your strategic focus or challenges:</p>
                  <Input
                    placeholder="e.g., 'I need to grow my customer base and improve retention'"
                    value={aiContext}
                    onChange={(e) => setAiContext(e.target.value)}
                    className="bg-slate-100 dark:bg-[#1A1A2E] border-purple-500/30"
                  />
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleAiSuggest('priority')} 
                      disabled={aiLoading.priority}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      {aiLoading.priority ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Generate Ideas
                    </Button>
                    <Button variant="ghost" onClick={() => { setShowAiContextInput(null); setAiContext(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button onClick={handleAddPriority} className="bg-purple-500 hover:bg-purple-600" data-testid="save-priority-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Priority
                </Button>
                <Button variant="ghost" onClick={() => { setShowAddPriority(false); setPriorityForm({ title: '', description: '', target_date: '' }); }}>
                  Cancel
                </Button>
                {showAiContextInput !== 'priority' && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAiContextInput('priority')}
                    className="ml-auto border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                    data-testid="ai-assist-priority-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI Suggest
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Priorities List */}
        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={objectives?.priorities?.map(p => p.id) || []}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {objectives?.priorities?.length === 0 && !showAddPriority && (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Flag className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No strategic priorities defined yet.</p>
                  <p className="text-sm">Add priorities to organize and track your goals.</p>
                </div>
              )}

              {objectives?.priorities?.map((priority, index) => (
                <SortablePriorityItem
                  key={priority.id}
                  priority={priority}
                  index={index}
                  expandedPriorities={expandedPriorities}
                  togglePriorityExpand={togglePriorityExpand}
                  startEditPriority={startEditPriority}
                  handleDeletePriority={handleDeletePriority}
                  editingPriorityId={editingPriorityId}
                  priorityForm={priorityForm}
                  setPriorityForm={setPriorityForm}
                  handleUpdatePriority={handleUpdatePriority}
                  formatDate={formatDate}
                  getProgressColor={getProgressColor}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
        </>
      )}
      
      {/* Vision Story Modal */}
      <Dialog open={showVisionStory} onOpenChange={setShowVisionStory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Play className="w-5 h-5 text-purple-500" />
              Play My Vision
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Format Selection */}
            {!visionStory && (
              <>
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Choose how to experience your vision:
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setStoryFormat('story')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        storyFormat === 'story' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <Heart className="w-6 h-6 text-purple-500" />
                      <span className="font-medium">Written Story</span>
                      <span className="text-xs text-slate-500">Read your future</span>
                    </button>
                    <button
                      onClick={() => setStoryFormat('audio')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        storyFormat === 'audio' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <Volume2 className="w-6 h-6 text-purple-500" />
                      <span className="font-medium">Audio Narration</span>
                      <span className="text-xs text-slate-500">Listen to your story</span>
                    </button>
                    <button
                      onClick={() => setStoryFormat('video')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        storyFormat === 'video' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <Video className="w-6 h-6 text-purple-500" />
                      <span className="font-medium">AI Video</span>
                      <span className="text-xs text-slate-500">Watch your vision</span>
                    </button>
                    <button
                      onClick={() => setStoryFormat('slideshow')}
                      className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                        storyFormat === 'slideshow' 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-purple-300'
                      }`}
                    >
                      <Image className="w-6 h-6 text-purple-500" />
                      <span className="font-medium">Visual Story</span>
                      <span className="text-xs text-slate-500">Images + text</span>
                    </button>
                  </div>
                </div>
                
                {/* Voice Selection for Audio */}
                {storyFormat === 'audio' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Choose narrator voice:
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['nova', 'alloy', 'echo', 'fable', 'onyx', 'shimmer'].map(voice => (
                        <button
                          key={voice}
                          onClick={() => setStoryVoice(voice)}
                          className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                            storyVoice === voice
                              ? 'bg-purple-500 text-white'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-purple-100'
                          }`}
                        >
                          {voice}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <Button
                  onClick={handleGenerateVisionStory}
                  disabled={storyLoading}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {storyLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating Your Vision...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate My Vision Story
                    </>
                  )}
                </Button>
              </>
            )}
            
            {/* Story Display */}
            {visionStory && (
              <div className="space-y-4">
                {/* Story Text */}
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                    <p className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                      {visionStory.story_text}
                    </p>
                  </div>
                </div>
                
                {/* Audio Player */}
                {visionStory.format === 'audio' && (
                  <div className="space-y-2">
                    {visionStory.audio_status === 'complete' && visionStory.audio_url ? (
                      <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
                        <p className="text-sm text-slate-500 mb-2">🎧 Listen to your vision:</p>
                        <audio controls className="w-full" src={visionStory.audio_url}>
                          Your browser does not support audio.
                        </audio>
                      </div>
                    ) : visionStory.audio_status === 'failed' ? (
                      <p className="text-sm text-red-500">Audio generation failed. Please try again.</p>
                    ) : (
                      <div className="flex items-center gap-2 text-purple-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating audio narration...</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Video Player */}
                {visionStory.format === 'video' && (
                  <div className="space-y-4">
                    {visionStory.video_status === 'complete' && visionStory.video_url ? (
                      <div className="space-y-4">
                        {/* Video Script (Vision/Mission being narrated) */}
                        {visionStory.video_script && (
                          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                            <p className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-2">📜 Your Vision & Mission:</p>
                            <p className="text-slate-700 dark:text-slate-300 italic">{visionStory.video_script}</p>
                          </div>
                        )}
                        
                        {/* Video Player */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
                          <p className="text-sm text-slate-500 mb-2">🎬 Watch your vision:</p>
                          <video controls className="w-full rounded-lg" src={visionStory.video_url}>
                            Your browser does not support video.
                          </video>
                        </div>
                        
                        {/* Audio Narration (separate, in case user wants to play audio over video) */}
                        {visionStory.video_audio_url && (
                          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-xl">
                            <p className="text-sm text-slate-500 mb-2">🎙️ Audio narration (play with video):</p>
                            <audio controls className="w-full">
                              <source src={visionStory.video_audio_url} type="audio/mpeg" />
                            </audio>
                            <p className="text-xs text-slate-400 mt-2">
                              Tip: Play the audio narration while watching the video for the full experience.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : visionStory.video_status === 'failed' ? (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                        <p className="text-sm text-red-500">Video generation failed: {visionStory.video_error || 'Unknown error'}. Please try again.</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-purple-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating video (this may take 2-4 minutes)...</span>
                      </div>
                    )}
                    
                    {/* Video supporting images */}
                    {visionStory.video_images && visionStory.video_images.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-500">🖼️ Vision gallery:</p>
                        <div className="grid grid-cols-3 gap-2">
                          {visionStory.video_images.map((img, idx) => (
                            <img 
                              key={idx} 
                              src={img} 
                              alt={`Vision scene ${idx + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-purple-200 dark:border-purple-800"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Slideshow with Images */}
                {visionStory.format === 'slideshow' && (
                  <div className="space-y-4">
                    {visionStory.slideshow_status === 'complete' && visionStory.slideshow_images?.length > 0 ? (
                      <div className="space-y-6">
                        {visionStory.story_text.split('\n\n').filter(p => p.trim()).map((paragraph, idx) => (
                          <div key={idx} className="flex gap-4 items-start">
                            {visionStory.slideshow_images[idx] && (
                              <img 
                                src={visionStory.slideshow_images[idx].url}
                                alt={`Vision scene ${idx + 1}`}
                                className="w-48 h-32 object-cover rounded-xl border-2 border-purple-300 dark:border-purple-700 flex-shrink-0"
                              />
                            )}
                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed flex-1">
                              {paragraph}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : visionStory.slideshow_status === 'failed' ? (
                      <p className="text-sm text-red-500">Image generation failed. You can still read the story above.</p>
                    ) : (
                      <div className="flex items-center gap-2 text-purple-500">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Generating inspiring images for your story...</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setVisionStory(null);
                      setPollingStory(null);
                    }}
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Generate New Story
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowVisionStory(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StrategicObjectives;
